import React from 'react';
import {
  Box,
  Breadcrumbs,
  Typography,
  Link as MuiLink,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Skeleton,
  Divider,
} from '@mui/material';
import { Link, useParams, useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useProjectStore } from '../../store/projectStore';
import { useLineStore } from '../../store/lineStore';
import { useAuthStore } from '../../store/authStore';
import { LineFormDialog } from '../../components/LineFormDialog';
import { DeleteConfirmationDialog } from '../../components/DeleteConfirmationDialog';
import { Line, LineCreate, LineUpdate } from '../../types/line';
import { isAdmin, isIssuer } from '../../utils/permissions';

export default function ProjectLines() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore(); // Get the current user from auth store
  const {
    currentProject,
    isLoading: projectLoading,
    error: projectError,
    fetchProject,
  } = useProjectStore();
  const {
    lines,
    isLoading: linesLoading,
    error: linesError,
    fetchLines,
    createLine,
    updateLine,
    deleteLine,
  } = useLineStore();

  const [isLineFormOpen, setIsLineFormOpen] = React.useState(false);
  const [selectedLine, setSelectedLine] = React.useState<Line | undefined>(undefined);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);

  React.useEffect(() => {
    if (id) {
      fetchProject(Number(id));
      fetchLines({ project_id: Number(id) });
    }
  }, [id, fetchProject, fetchLines]);

  const handleCreateLineClick = () => {
    setSelectedLine(undefined);
    setIsLineFormOpen(true);
  };

  const handleEditLineClick = (line: Line) => {
    setSelectedLine(line);
    setIsLineFormOpen(true);
  };

  const handleDeleteLineClick = (line: Line) => {
    setSelectedLine(line);
    setIsDeleteDialogOpen(true);
  };

  const handleLineFormClose = () => {
    setIsLineFormOpen(false);
    setSelectedLine(undefined);
  };

  const handleDeleteDialogClose = () => {
    setIsDeleteDialogOpen(false);
    setSelectedLine(undefined);
  };

  const handleLineFormSubmit = async (formData: Partial<Line>) => {
    try {
      if (selectedLine) {
        await updateLine(selectedLine.id, formData as LineUpdate);
      } else if (id) {
        const lineData: LineCreate = {
          name: formData.name!,
          description: formData.description,
          status: formData.status ?? true,
          project_id: Number(id)
        };
        await createLine(lineData);
      }
      handleLineFormClose();
      fetchLines({ project_id: Number(id) });
    } catch (error) {
      // Error will be handled by the store
    }
  };

  if (projectLoading) {
    return <Typography>Loading...</Typography>;
  }

  if (!currentProject) {
    return <Typography color="error">Project not found</Typography>;
  }

  return (
    <>
      <Box sx={{ mb: 4 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <MuiLink component={Link} to="/projects" color="inherit">
            Events
          </MuiLink>
          <MuiLink component={Link} to={`/projects/${id}`} color="inherit">
            {currentProject.name}
          </MuiLink>
          <Typography color="text.primary">Lines</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Lines
          </Typography>
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            onClick={handleCreateLineClick}
          >
            Add Line
          </Button>
        </Box>

        <Paper sx={{ p: 3 }}>
          {linesLoading ? (
            <Skeleton variant="rectangular" height={200} />
          ) : linesError ? (
            <Typography color="error">{linesError}</Typography>
          ) : (
            <TableContainer>
              <Table>
                <TableHead sx={{ backgroundColor: '#e0e0e0' }}>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lines.map((line) => (
                    <TableRow key={line.id}>
                      <TableCell>{line.name}</TableCell>
                      <TableCell>{line.description || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={line.status ? 'Active' : 'Inactive'}
                          color={line.status ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          {(isAdmin(user) || isIssuer(user)) && (
                            <IconButton
                              size="small"
                              onClick={() => handleEditLineClick(line)}
                            >
                              <EditIcon />
                            </IconButton>
                          )}
                          {(isAdmin(user) || isIssuer(user)) && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteLineClick(line)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {lines.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        No lines found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>

      {/* Dialogs */}
      <LineFormDialog
        open={isLineFormOpen}
        onClose={handleLineFormClose}
        onSubmit={handleLineFormSubmit}
        projectId={Number(id)}
        line={selectedLine}
      />
      <DeleteConfirmationDialog
        open={isDeleteDialogOpen}
        onClose={handleDeleteDialogClose}
        onConfirm={async () => {
          if (selectedLine) {
            await deleteLine(selectedLine.id);
            handleDeleteDialogClose();
            fetchLines({ project_id: Number(id) });
          }
        }}
        title="Delete Line"
        message="Are you sure you want to delete this line? This action cannot be undone."
      />
    </>
  );
}
